/**
 * THE JOB, END TO END — the shop owner's map of the whole process.
 *
 * Anthony wrote the flow out and asked what was missing. Three things
 * were, and they are marked on this page rather than quietly folded in,
 * because a shop owner reading his own process wants to see what changed:
 *
 *   1. THE DIAGNOSIS LOOP. His flow runs quote → approve → start →
 *      inspect → diagnose. That works for known work: brakes, an oil
 *      change, a water pump. It cannot work for "it makes a noise",
 *      which is a large share of what comes in — you cannot price a
 *      repair you have not identified. Those jobs quote ONE HOUR OF
 *      DIAGNOSIS, get a yes for that, and then come back round for a
 *      second quote and a second yes. The loop is the normal case, not
 *      the exception.
 *
 *   2. AN ADD-ON IS A NEW YES, not an edit. "Add on to quote before
 *      repair" has to create a fresh authorization at the new figure.
 *      The database already refuses two authorizations for the same
 *      amount and allows one at a revised amount, so the shape is
 *      enforced — but the process has to say it out loud, because the
 *      temptation at a busy counter is to change the number and carry on.
 *
 *   3. PHOTOS AFTER, not only before. His flow has before-work photos.
 *      The after shot is the one that settles a dispute and the one the
 *      customer actually wants to see.
 *
 * The diagram is inline SVG built from the same array the steps are, so
 * the picture cannot drift from the words underneath it.
 */

import { esc, mount } from "../ui/dom.ts";
import { say, shopWords } from "../ui/words.ts";
import { cap } from "../domain/trade.ts";
import { gap, gapsOn, lastChecked } from "../domain/gaps.ts";
import { LANES } from "../domain/rental.ts";

type Phase = {
  n: number;
  title: string;
  who: "Desk" | "Tech" | "Customer" | "System";
  body: string;
  /** Flagged where the system enforces something rather than suggesting it. */
  rule?: string;
  /** Named where Anthony's original flow did not have it. */
  added?: string;
  photo?: { src: string; cap: string };
};

/**
 * WHOSE STEP IT IS, IN THE SHOP'S OWN WORD.
 *
 * The four values are keys and three of them are the same word in every
 * trade. "Tech" is not: NYIA's people are investigators, and a tag reading
 * TECH down the side of an investigator's own process map is the same
 * fault as "Get the parts" six steps later. It is read at render for the
 * reason phases() is: a const would freeze on auto before anybody signed
 * in.
 *
 * The chip is minmax(150px,1fr) and the tag is 9.5px uppercase, so the
 * longest word any trade has here still sits on one line.
 */
const whoLabel = (who: Phase["who"]): string => {
  if (who !== "Tech") return who;
  /* "Tech" is the short form of "technician", and four of the five trades
     call their people exactly that, so they keep the four letter tag they
     have always read here. Only a trade whose people are called something
     else gets a different word, which is the whole change. */
  const t = shopWords().techLabel;
  return t === "technician" ? "Tech" : cap(t);
};

const WHO_COLOUR: Record<Phase["who"], string> = {
  Desk: "var(--green)",
  Tech: "var(--amber)",
  Customer: "var(--purple)",
  System: "#60a5fa",
};

/**
 * ─── THE SPINE IS BUILT AT RENDER, FOR THE SHOP READING IT ─────────────
 *
 * Anthony, 2026-09-18: "I need the run books to match the companies /
 * trades."
 *
 * This was a module-level const and every word of it was a garage. A
 * locksmith opening "The job, end to end" read step 3 "Get the car", step
 * 15 "A plate or a VIN, on file, before this job can close", and six
 * photographs of a 1984 Camaro having its carburetor rebuilt. Step 15 was
 * not merely the wrong noun: isIdentified() does not look at
 * license_plate for a trade with hasPlate false, so the manual was telling
 * four of the five trades to satisfy a gate with the one field that
 * cannot satisfy it. That is the same fault f74b21c just took off the job
 * screens, still sitting in the manual that explains them.
 *
 * A FUNCTION AND NOT A CONST, for the reason runbook.ts records: a const
 * at module scope is evaluated on first import, which is before anybody
 * has signed in, so every string would freeze on the auto fallback.
 *
 * AUTO'S COPY IS THE LITERAL ORIGINAL wherever its wording is idiomatic
 * rather than substitutable -- "car", "keys back", "before a wrench
 * moves", the tread depth on the tire section. hasEngine is the key, as
 * it is in the runbook: true for auto alone, and exactly the condition
 * under which the engine idiom and the garage photographs apply.
 */
export function phases(): Phase[] {
  const w = shopWords();
  return [
  { n: 1, title: "A lead arrives", who: "Desk",
    body: "Somebody rings, texts, fills in a form or emails. Take the name, the number and what they think is wrong. Nothing else matters yet." },

  { n: 2, title: "Somebody rings them back", who: "Desk",
    body: "The lead moves from new to contacted the moment a person reaches out. After seven days with no quote it closes itself as lost, because by then they have gone somewhere else.",
    rule: "Leads show newest first, and every card says whether anybody has contacted them." },

  w.hasEngine
    ? { n: 3, title: "Get the car", who: "Desk",
      body: "Year, make, model, and ask for the plate or the VIN now, while you have them on the phone. It is one more question on a call you are already having.",
      added: "Mileage too, if they know it. Warranty and service reminders both need it.",
      photo: { src: "/images/garage/camaro-1-arrival.jpg", cap: "A 1984 Camaro Z28, in on a fresh carb and distributor that would not run right" } }
    : { n: 3, title: `Get the ${w.thing}`, who: "Desk",
      /* What to ask for is read from identifyAsks() by way of identifySub,
         so the question on the phone is the question the closing gate will
         actually accept. Asking a locksmith's desk for a plate produced a
         step with no exit. */
      body: `${w.yearLabel}, ${w.makeLabel.toLowerCase()}, ${w.modelLabel.toLowerCase()}, `
        + `and ask for the ${w.serial} now, while you have them on the phone. It is one `
        + `more question on a call you are already having.`,
      added: `${w.identifySub} Getting it at this point is the difference between a `
        + `question on the phone and a job that will not close at the end of the week.` },

  /* A TRADE WHOSE QUOTE IS A FREE VISIT says so here, because the step
     below would otherwise teach a door shop to price a door off a phone
     call. Anthony, 2026-09-29: "we just want a quote to be the customer
     saying yes to a free visit for an estimate ... Next step is schedule
     still." Every other trade reads exactly what it read before. */
  { n: 4, title: w.quoteIsVisit ? "Offer the free estimate visit" : "Quote it, and there are two kinds",
    who: "Desk",
    body: w.quoteIsVisit
      ? "The first quote is not a price. It is an offer to come out and look for free, one line at nothing, and because there is nothing on it for a person to check it can go to them by text on its own. Their YES is the approval. The real price is written at the {thing}: KNOWN WORK, price it; UNKNOWN WORK, price ONE HOUR OF LOOKING AT IT. That price gets its own yes."
      : w.hasEngine
      ? "KNOWN WORK, like brakes or a water pump: price the repair. UNKNOWN NOISE: price ONE HOUR OF DIAGNOSIS and nothing else. You cannot quote a repair you have not identified, and a guess you have to walk back costs more than the hour did."
      : "KNOWN WORK, where you already know what is wrong: price it. UNKNOWN WORK, where you do not: price ONE HOUR OF LOOKING AT IT and nothing else. You cannot quote work you have not identified, and a guess you have to walk back costs more than the hour did.",
    added: "This split was not in the original flow. It is the reason the loop at step 11 exists.",
    rule: "{Parts} price at what the customer pays. They never see your cost." },

  { n: 5, title: "The customer answers", who: "Customer",
    body: "Three ways it can go. APPROVED means record the yes, signed on screen or spoken on the phone with their name on it. WANTS CHANGES means revise it and send it again, and the new price needs its own yes. CANCELLED means close it with a reason, so that next year you know why.",
    rule: "A spoken yes is valid. A nameless one is not. The job has to say who agreed." },

  { n: 6, title: "Book it", who: "Desk",
    body: "Pick the date and say whether it is mobile or in the shop.",
    rule: "Approval is not a booking. A job cannot be marked Booked without a date, because the database refuses it, or it sits on no board and nobody drives to it." },

  { n: 7, title: "Both ends confirm", who: "System",
    body: "The {tech} confirms they will be there. The customer confirms they will be there. A job where only one of them knows the time is a wasted morning." },

  { n: 8, title: "Start the work", who: "Tech",
    body: "Not before. The five stars at the top of the job card say whether the yes is on file, and if Approved is not lit the work is not yours to start.",
    rule: "No authorization, no start. A service advisor or admin can step past it, and their name and reason go on the job for good." },

  { n: 9, title: "Photos and the inspection, before anything is touched", who: "Tech",
    body: "What it looked like when it arrived. This is the record that answers 'it was like that when I brought it in' three months later, and it is most of what the customer sees on their own page. The inspection sheet is on the job itself under Work, and it saves as you go, so {atech} can put the phone down partway through and pick it back up.",
    rule: w.hasEngine
      ? "Nine sections, and four verdicts on every point. Good, keep an eye on it, needs doing now, or does not apply. Anything worse than good has to say why before it saves. Tires are optional and take a tread depth in thirty seconds of an inch, which grades itself."
      : "Nine sections, and four verdicts on every point. Good, keep an eye on it, needs doing now, or does not apply. Anything worse than good has to say why before it saves.",
    ...(w.hasEngine
      ? { photo: { src: "/images/garage/camaro-2-before.jpg", cap: "Fender covers down, hood up, nothing touched yet" } }
      : {}) },

  { n: 10, title: "The diagnostic card", who: "Tech",
    body: "Mostly the {tech}'s own words, plus one main photo of the trouble spot. This is what the desk quotes from, so it has to say what is wrong, not just what it looks like. It has its own screen. Tap Diagnostic card on the job, and {atech} can add to it any time. The customer reads it on their own page too, unless the shop hides it.",
    ...(w.hasEngine
      ? { photo: { src: "/images/garage/camaro-3-fault.jpg", cap: "A finger on the actual spot. One shot like this beats a paragraph describing it." } }
      : {}) },

  { n: 11, title: "The fork, and this is the one that costs money", who: "Desk",
    body: w.hasEngine
      ? "Is the repair inside what was already approved? If YES, carry on. If NO, stop. A new quote, sent, and a new yes at the new figure before a wrench moves. Working past an approved number is how a shop ends up eating the difference or arguing about it."
      : "Is the work inside what was already approved? If YES, carry on. If NO, stop. A new quote, sent, and a new yes at the new figure before anybody carries on. Working past an approved number is how a shop ends up eating the difference or arguing about it.",
    added: "Diagnosis-only jobs come back through here for their real quote. That is the loop, and it is normal.",
    rule: "The database refuses a second authorization at the same amount and accepts one at a revised amount. Two prices means two answers." },

  /* ─── A LINE YOU ORDER, OR A LINE YOU RUN UP ────────────────────────
     partNumberLabel is null for one kind of trade: the one whose
     non-labour lines have no catalog number, because nobody sells them
     out of a catalog. NYIA's are a vehicle fee per day, database searches
     and filing fees. You do not order a filing fee and you never wait on
     one, so the step is reworded and the gap under it goes with it. A
     manual promising to build a waiting-on-parts state to a firm that
     orders nothing is describing somebody else's shop.

     hasPartsCatalog is the WRONG key here and the two must not be
     collapsed, as src/domain/trade.ts says at the flag itself: a
     locksmith has no vehicle catalog and still orders real parts with
     real part numbers, and still waits on them. */
  w.partNumberLabel
    ? { n: 12, title: "Get the {parts}", who: "Desk",
      body: "Order against the approved figure. Real cost goes on the line and the customer sees the sell price.",
      /* THE SAME CLAIM WAS WRITTEN TWICE, here and in the card at the bottom
         of this page, in two different sets of words. That is how a claim
         gets half corrected: somebody fixes the card and this keeps saying
         the old thing, which is exactly what happened to the inspection line
         on 2026-09-17. One entry in src/domain/gaps.ts, read in both
         places, and the check fails if it ever comes true. */
      /* In the trade's own word, through say(): a remodeler waits on a
         material, not a part (SRC, 2026-10-23). Auto reads it unchanged. */
      added: gap("waiting-on-parts-status").replace(/\bpart\b/g, "{part}") }
    : { n: 12, title: "The {parts}", who: "Desk",
      body: "Put them on the job as they happen, against the approved figure. What it cost goes on the line and the customer sees what they are charged." },

  { n: 13, title: w.hasEngine ? "Do the repair" : "Do the work", who: "Tech",
    body: "Keep shooting as you go. The mid-job photos are the ones nobody thinks to take and everybody wants later.",
    ...(w.hasEngine
      ? { photo: { src: "/images/garage/camaro-4-work.jpg", cap: "The carburetor off and in hand, partway through" } }
      : {}) },

  { n: 14, title: "Photos after", who: "Tech",
    body: w.hasEngine
      ? "The finished work, and the old part next to the new one. This is what the customer opens their page to see, and it is what ends an argument before it starts."
      : "The finished work, and whatever came out next to whatever went in. This is what the customer opens their page to see, and it is what ends an argument before it starts.",
    added: "The original flow had photos before the work but not after. The after shot is the one that does the work.",
    ...(w.hasEngine
      ? { photo: { src: "/images/garage/camaro-5-done.jpg", cap: "New cap and leads on, running right. This is the one the customer opens their page to see." } }
      : {}) },

  /* THE STEP THAT WAS NOT JUST THE WRONG NOUN. identifySub is the same
     sentence the job screen shows over the same boxes, and identifyBlocked
     is what the shop reads when the gate refuses -- so the manual, the form
     and the refusal now say one thing. Hardcoding "a plate or a VIN" here
     sent four trades at a field isIdentified() never reads. */
  { n: 15, title: `${w.identifyTitle} has to be identified`, who: "System",
    /* identifyTitle lowercased, not `thing`, so auto keeps the word the
       shop has always read here: "next to the car", not "the vehicle". */
    body: `${w.identifySub} Ideally taken at step 3, but by now somebody has stood next `
      + `to ${w.identifyTitle.toLowerCase()} and can read it off.`,
    rule: `The system will not let you mark a job completed, invoiced or paid without it. `
      + `${w.identifyBlocked} A job on an unidentified ${w.thing} cannot be warrantied, `
      + `looked up, or defended.` },

  { n: 16, title: "Invoice", who: "Desk",
    body: "The total comes off the lines. Nobody types it, so it cannot disagree with what the customer approved." },

  { n: 17, title: "They pay", who: "Customer",
    body: "Card, cash, check or transfer, and record which. Cash written up properly is the difference between a real revenue figure and a guess.",
    rule: "Reports count money from invoices and payments, never from a job's status." },

  w.hasEngine
    ? { n: 18, title: "Keys back, and paid", who: "Desk",
      body: "Finished and collected are not the same thing. A car sitting in the lot with the work done is still your problem until it drives away.",
      added: "Handover was not a step in the original flow. It is the last place a job goes quiet.",
      photo: { src: "/images/garage/camaro-6-keys.jpg", cap: "How the owner gets it back, rag off the seat and all" } }
    : { n: 18, title: "Handed back, and paid", who: "Desk",
      body: `Finished and handed over are not the same thing. Work that is done and has `
        + `not been signed off with the customer is still your problem.`,
      added: "Handover was not a step in the original flow. It is the last place a job goes quiet." },
  ];
}

/* ── the map ──────────────────────────────────────────────────────── */

/**
 * Built from the same array the steps are, so the map cannot drift from the steps beneath it.
 *
 * This was an SVG 940 pixels wide in a sideways scroller, which on a phone
 * is a strip of four pixel labels. These are chips that wrap, and every one
 * of them is a link to its own step, so the thing that was only a picture
 * is now how you get around the page.
 */
function processMap(all: Phase[]): string {
  const chip = (p: Phase) => `
    <a class="wfchip" href="#wfstep-${p.n}" style="--who:${WHO_COLOUR[p.who]}">
      <span class="wfchip-h"><span class="wfchip-n">${p.n}</span>
        <span class="wfchip-w">${esc(whoLabel(p.who))}</span></span>
      <span class="wfchip-t">${esc(say(p.title))}</span>
    </a>`;

  /* Split at the fork, because the loop goes between the two halves. */
  const upTo = all.filter((p) => p.n <= 11).map(chip).join("");
  const after = all.filter((p) => p.n > 11).map(chip).join("");

  return `
    <nav class="wfmap" aria-label="The eighteen steps, each one a link to its detail">
      ${upTo}</nav>
    <p class="wfloop"><b>The loop.</b> More work than was quoted, or a diagnosis
      that is now finished, goes back to step 4 for a new price and a new yes
      before anybody carries on.</p>
    <nav class="wfmap" aria-label="The rest of the steps">${after}</nav>
    <div class="wflegend">
      ${(["Desk", "Tech", "Customer", "System"] as const).map((k) =>
        `<span><i style="background:${WHO_COLOUR[k]}"></i>${esc(whoLabel(k))}</span>`).join("")}
    </div>`;
}

/**
 * WHAT IS STILL MISSING — the only card in this app that claims something
 * does NOT exist, and the reason src/domain/gaps.ts exists.
 *
 * This was two sentences typed by hand. On 2026-09-17 they still said the
 * inspection sheet was not built and that photos could not be uploaded,
 * months after both shipped, and Anthony asked why his inspection
 * checklist had been deleted. It had not. He was reading the
 * documentation as the product, which is the only thing a shop owner can
 * reasonably do with a card in his own software.
 *
 * Every other card on every other screen shows what EXISTS, so it cannot
 * lie for long. This one had nothing that would go with the feature, so
 * it went stale in silence.
 *
 * It renders from the registry now, and every entry there carries the
 * patterns that would mean it is finished. `npm run check` runs them, so
 * the commit that builds one of these is the commit that fails.
 *
 * THE DATE IS ON THE SCREEN on purpose. The reader is the person who can
 * tell it is wrong -- Anthony knew the inspection was built. What he could
 * not see was how long the sentence saying otherwise had been sitting
 * there. Nothing to show means no card at all, because an empty card with
 * a heading reads as a screen that failed to load.
 */
function missingCard(): string {
  /* A gap is hidden from a trade it cannot happen to, the same way the
     catalog gaps are hidden from a trade with no vehicles. NYIA orders
     nothing, so nothing is ever waiting on an order, and the step above
     drops its Added line for the same reason. The card and the step read
     one registry, which is the whole point of src/domain/gaps.ts. */
  const gaps = gapsOn("workflow", true, Boolean(shopWords().partNumberLabel));
  if (!gaps.length) return "";
  const when = lastChecked(gaps);
  return `<div class="card"><div class="card-head">What is still missing</div>
      ${gaps.map((g) => `<div class="wt-trap">${esc(say(g.body.replace(/\bpart\b/g, "{part}")))}</div>`).join("")}
      ${when ? `<div class="pad"><p class="quiet">Read against the code on ${esc(when)}.
        Anything built since then fails the build rather than sitting here.</p></div>` : ""}
    </div>`;
}

/**
 * HOW A RENTAL RUNS, for a shop on the Rentals side.
 *
 * Anthony, 2026-09-28: the process map of an RV rental yard is nine lanes,
 * not the eighteen repair steps. A shop with a rentals side sees this when
 * it is on Rentals and the eighteen steps when it is on Repair, from the
 * same More row, because the question "how does this work" is about the
 * side you are standing on.
 *
 * Built from LANES in src/domain/rental.ts, the same array the board
 * draws its columns from, so the manual and the board cannot drift apart.
 * The rule line is only on a lane where the system holds the rule.
 */
const LANE_COLOUR: Record<(typeof LANES)[number]["who"], string> = {
  Desk: "var(--green)", Yard: "var(--amber)", Renter: "var(--purple)", System: "#60a5fa",
};

function rentalFlow(): void {
  mount(`<div class="page">
    <a class="back" href="#/rentals">&larr; The board</a>
    <header class="page-head">
      <h1>How a Rental Runs</h1>
      <p>Nine lanes from somebody asking to the deposit coming back. A trip can be cancelled
         any time before it goes out. Archive on a trip puts it away in one press: Cancelled if the
         RV never left, Closed if it did.</p>
    </header>
    <nav class="wfmap" aria-label="The nine lanes, each one a link to its detail">
      ${LANES.map((l, i) => `
        <a class="wfchip" href="#wfstep-${i + 1}" style="--who:${LANE_COLOUR[l.who]}">
          <span class="wfchip-h"><span class="wfchip-n">${i + 1}</span>
            <span class="wfchip-w">${esc(l.who)}</span></span>
          <span class="wfchip-t">${esc(l.label)}</span>
        </a>`).join("")}</nav>
    <div class="wflegend">
      ${(["Desk", "Yard", "Renter", "System"] as const).map((k) =>
        `<span><i style="background:${LANE_COLOUR[k]}"></i>${esc(k)}</span>`).join("")}
    </div>
    <div class="wfsteps">
      ${LANES.map((l, i) => `
        <div class="wfstep" id="wfstep-${i + 1}" style="--who:${LANE_COLOUR[l.who]}">
          <div class="wfstep-h">
            <span class="wfstep-n">${i + 1}</span>
            <span class="wfstep-t">${esc(l.label)}</span>
            <span class="wfstep-w">${esc(l.who)}</span>
          </div>
          <p class="wfstep-b">${esc(l.line)}</p>
          ${l.rule ? `<div class="wfstep-rule"><b>The system enforces this.</b> ${esc(l.rule)}</div>` : ""}
        </div>`).join("")}
    </div>
  </div>`);
}

export function workflowPage(side: "rentals" | "repair" = "repair"): void {
  if (side === "rentals") return rentalFlow();
  const all = phases();
  /* The heading was still handing keys back to a firm whose step 18 says
     "Handed back, and paid" -- the summary at the top disagreeing with the
     step it summarises. Auto keeps its own idiom, as everywhere else here. */
  const ending = shopWords().hasEngine ? "the keys going back" : "the work handed back";
  mount(`<div class="page">
    <a class="back" href="#/walkthroughs">&larr; All walkthroughs</a>
    <header class="page-head">
      <h1>The job, end to end</h1>
      <p>Eighteen steps from somebody ringing to ${esc(ending)}. Six carry something
         that was not in the original write-up, marked <b class="addedmark">Added</b> &mdash;
         three of those matter a lot and three are small.</p>
    </header>

    ${processMap(all)}

    <div class="wfsteps">
      ${all.map((p) => `
        <div class="wfstep" id="wfstep-${p.n}" style="--who:${WHO_COLOUR[p.who]}">
          <div class="wfstep-h">
            <span class="wfstep-n">${p.n}</span>
            <span class="wfstep-t">${esc(say(p.title))}</span>
            <span class="wfstep-w">${esc(whoLabel(p.who))}</span>
          </div>
          <p class="wfstep-b">${esc(say(p.body))}</p>
          ${p.rule ? `<div class="wfstep-rule"><b>The system enforces this.</b> ${esc(say(p.rule))}</div>` : ""}
          ${p.added ? `<div class="wfstep-add"><b>Added.</b> ${esc(say(p.added))}</div>` : ""}
          ${p.photo ? `<figure class="wfstep-photo">
            <img loading="lazy" src="${esc(p.photo.src)}" alt="${esc(say(p.photo.cap))}">
            <figcaption>${esc(say(p.photo.cap))}</figcaption></figure>` : ""}
        </div>`).join("")}
    </div>

    ${missingCard()}
  </div>`);
}
